import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, Search, Layers, CheckCircle2, Send, RotateCcw, XCircle, BookOpen,
} from 'lucide-react';

import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { AppRoutes }     from '@/constants/routes';
import { Spinner }       from '@/components/ui/Spinner';
import { ListPager } from '@/components/lists/ListPager';
import { cn }            from '@/utils/cn';
import { apiClient }     from '@/api/client';

// Mirrors myLedger IntegrationPostingListDto.
interface IntegrationBatchListDto {
  id: string;
  batchNumber: string;
  sourceSystem: string;
  sourceModule: string;
  sourceTransactionType: string;
  sourceDocumentNumber: string;
  status: string;
  totalDebits: number;
  totalCredits: number;
  currencyCode: string;
  retryCount: number;
  generatedJournalId?: string | null;
  postedDate?: string | null;
  submittedDate: string;
}

const STATUS_COLORS: Record<string, string> = {
  Pending:    'bg-amber-100 dark:bg-amber-900/30  text-amber-700 dark:text-amber-400',
  Validated:  'bg-blue-100 dark:bg-blue-900/30   text-blue-700 dark:text-blue-400',
  Processing: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
  Posted:     'bg-teal-100 dark:bg-teal-900/30   text-teal-700 dark:text-teal-400',
  Failed:     'bg-red-100 dark:bg-red-900/30    text-red-700 dark:text-red-400',
  Cancelled:  'bg-slate-100 dark:bg-slate-800  text-slate-600 dark:text-gray-400',
  Retrying:   'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  DeadLetter: 'bg-rose-200   text-rose-800',
};

const STATUSES = ['Pending', 'Validated', 'Processing', 'Posted', 'Failed', 'Retrying', 'Cancelled', 'DeadLetter'];

/** The server's own reason where it gave one, else a plain fallback. */
const messageOf = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

export const IntegrationBatchListPage = () => {
  const { hasPermission } = usePermission();
  const navigate = useNavigate();

  // Open the double-entry posting details (the generated GL journal) in the ledger view.
  const openLedger = (item: IntegrationBatchListDto) => {
    if (item.generatedJournalId)
      navigate(AppRoutes.GL_JOURNAL_DETAIL.replace(':id', item.generatedJournalId));
  };

  const canValidate = hasPermission(Permissions.INTEGRATION_POSTING_VALIDATE);
  const canPost     = hasPermission(Permissions.INTEGRATION_POSTING_POST);
  const canRetry    = hasPermission(Permissions.INTEGRATION_POSTING_RETRY);
  const canCancel   = hasPermission(Permissions.INTEGRATION_POSTING_CANCEL);

  const [items,   setItems]   = useState<IntegrationBatchListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [error,   setError]   = useState<string | null>(null);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const load = useCallback(async (p: number, statusFilter: string, searchText: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('pageNumber', String(p));
      params.set('pageSize', String(pageSize));
      if (statusFilter) params.set('status', statusFilter);
      if (searchText)   params.set('search', searchText);

      // Correct route: /integration-posting-batches (company is forced from the token).
      const res  = await apiClient.get(`/integration-posting-batches?${params}`);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(data?.totalCount ?? 0);
    } catch (err) {
      setError(messageOf(err, 'Could not load integration batches.'));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, status, search); }, [page, status, search, load]);

  const doAction = async (id: string, action: 'validate' | 'post' | 'retry' | 'cancel') => {
    setError(null);
    try {
      const body = action === 'cancel' ? { reason: 'Cancelled from console' } : undefined;
      await apiClient.post(`/integration-posting-batches/${id}/${action}`, body);
      load(page, status, search);
    } catch (err) {
      setError(messageOf(err, `Could not ${action} the batch.`));
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtAmt  = (n: number) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 });


  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Layers size={20} className="text-signal" />
              Integration Posting Batches
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} batches posted by connected systems
            </p>
          </div>
          <button onClick={() => load(page, status, search)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors" title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(1); } }}
              placeholder="Search batch # or document… (Enter)"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-signal focus:border-transparent outline-none"
            />
          </div>
          <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2
                       bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-signal outline-none">
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {error && (
          <div role="alert" className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Layers size={40} className="mb-3 opacity-40" />
            {search || status ? (
              <p className="text-sm font-medium">No batches match these filters</p>
            ) : (
              <>
                <p className="text-sm font-medium">No integration batches yet</p>
                <p className="text-xs mt-1">Batches appear here once a connected system posts transactions.</p>
              </>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Batch #</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Source</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Document</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Debits</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Credits</th>
                <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Submitted</th>
                <th className="px-4 py-3 text-right  text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(item => (
                <tr key={item.id}
                  onClick={() => openLedger(item)}
                  className={cn(
                    'hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors',
                    item.generatedJournalId && 'cursor-pointer')}
                  title={item.generatedJournalId ? 'Open double-entry posting in the ledger' : undefined}>
                  <td className="px-4 py-3 font-mono font-semibold text-signal">
                    <span className="inline-flex items-center gap-1.5">
                      {item.batchNumber}
                      {item.generatedJournalId && <BookOpen size={13} className="text-slate-400" />}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-mono font-medium text-slate-700 dark:text-slate-300">
                      {item.sourceTransactionType || item.sourceModule}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs max-w-[180px] truncate">{item.sourceDocumentNumber}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[item.status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">{fmtAmt(item.totalDebits)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">{fmtAmt(item.totalCredits)}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{fmtDate(item.submittedDate)}</td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {canValidate && item.status === 'Pending' && (
                        <button onClick={() => doAction(item.id, 'validate')} title="Validate"
                          className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-200 rounded transition-colors flex items-center gap-0.5">
                          <CheckCircle2 size={11} /> Validate
                        </button>
                      )}
                      {canPost && item.status === 'Validated' && (
                        <button onClick={() => doAction(item.id, 'post')} title="Post"
                          className="px-2 py-1 text-xs bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 hover:bg-teal-200 rounded transition-colors flex items-center gap-0.5">
                          <Send size={11} /> Post
                        </button>
                      )}
                      {canRetry && (item.status === 'Failed' || item.status === 'DeadLetter') && (
                        <button onClick={() => doAction(item.id, 'retry')} title="Retry"
                          className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 hover:bg-purple-200 rounded transition-colors flex items-center gap-0.5">
                          <RotateCcw size={11} /> Retry
                        </button>
                      )}
                      {canCancel && ['Pending', 'Validated', 'Failed'].includes(item.status) && (
                        <button onClick={() => doAction(item.id, 'cancel')} title="Cancel"
                          className="p-1.5 rounded hover:bg-red-100 text-red-500 transition-colors">
                          <XCircle size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ListPager page={page} totalPages={totalPages} totalCount={total} onChange={setPage} noun="batches" />
    </div>
  );
};
